import { useAppSelector } from "@/redux/hooks";
import { useTheme } from "@mui/material";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getHeadersWithUniqueVisitorId } from "@brandclub/common-ui";
import { IconCompose, IconSidebar } from "./ConciergeIcons";

interface Conversation {
  conversationId: string;
  preview: string;
  title?: string;
  lastUpdated: string;
}

interface ConciergeSidebarProps {
  onConversationSelect?: (conversationId: string) => void;
  onComposeClick?: () => void;
  currentConversationId?: string | null;
}

const fetchConversations = async (): Promise<{
  conversations: Conversation[];
}> => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch("/api-rewards/concierge/conversations", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch conversations");
  }

  return response.json();
};

interface SidebarButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  isExpanded: boolean;
  ariaLabel: string;
  textColor?: string;
}

const SidebarButton = ({
  icon,
  label,
  onClick,
  isExpanded,
  ariaLabel,
  textColor,
}: SidebarButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="tw:relative">
      <button
        onClick={onClick}
        aria-label={ariaLabel}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:rounded tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:w-full"
      >
        <div className="tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:size-5">
          {icon}
        </div>
        <span
          className="tw:text-sm tw:font-medium tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150"
          style={{
            opacity: isExpanded ? 1 : 0,
            width: isExpanded ? "auto" : 0,
            color: textColor,
          }}
        >
          {label}
        </span>
      </button>
      {/* Tooltip - only show when collapsed and hovered */}
      {!isExpanded && isHovered && (
        <div
          className="tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:bg-primary tw:text-white tw:rounded-lg tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap"
          style={{
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "18px",
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
};

export const ConciergeSidebar = ({
  onConversationSelect,
  onComposeClick,
  currentConversationId,
}: ConciergeSidebarProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const theme = useTheme();

  const { data } = useQuery<{
    conversations: Conversation[];
  }>({
    queryKey: ["conversations"],
    queryFn: fetchConversations,
    enabled: isExpanded,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const getInitials = () => {
    const firstName = userProfile?.given_name || "";
    const lastName = userProfile?.family_name || "";
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.slice(0, 2).toUpperCase();
    }
    return "";
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleConversationClick = (conversationId: string) => {
    onConversationSelect?.(conversationId);
  };

  const initials = getInitials();
  const isSignedIn = !!userProfile?.userId;

  const [avatarHovered, setAvatarHovered] = useState(false);

  return (
    <aside
      className="tw:hidden tw:md:flex tw:flex-col tw:shrink-0 tw:h-full tw:border-r tw:border-[#dcdddc]"
      style={{
        backgroundColor: theme.palette.background.default,
        width: isExpanded ? 248 : 40,
        transition: "width 0.25s ease-in-out",
      }}
    >
      {/* Top icons */}
      <div className="tw:flex tw:flex-col tw:pt-3">
        {/* Toggle sidebar */}
        <SidebarButton
          icon={
            <IconSidebar
              size={20}
              style={{ color: theme.palette.text.primary }}
            />
          }
          label={isExpanded ? "Close sidebar" : "Open sidebar"}
          onClick={handleToggle}
          isExpanded={isExpanded}
          ariaLabel={isExpanded ? "Close sidebar" : "Open sidebar"}
          textColor={theme.palette.text.primary}
        />

        {/* Compose new conversation */}
        <SidebarButton
          icon={
            <IconCompose
              size={18}
              style={{ color: theme.palette.text.primary }}
            />
          }
          label="New chat"
          onClick={onComposeClick}
          isExpanded={isExpanded}
          ariaLabel="Start new chat"
          textColor={theme.palette.text.primary}
        />

        {/* Shopping cart
        <SidebarButton
          icon={
            <IconShoppingBag
              size={20}
              style={{ color: theme.palette.text.primary }}
            />
          }
          label="Products for you"
          onClick={handleBagClick}
          isExpanded={isExpanded}
          ariaLabel="Products for you"
        /> */}
      </div>

      {/* Chat history - only visible when expanded, takes remaining space */}
      {isExpanded ? (
        <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:mt-4 tw:overflow-hidden">
          <div className="tw:px-2.5 tw:text-sm tw:font-medium tw:text-primary tw:shrink-0">
            Chats
          </div>
          <div className="tw:flex-1 tw:overflow-y-auto tw:mt-2">
            {data?.conversations?.map((conversation) => {
              const isActive =
                conversation.conversationId === currentConversationId;
              return (
                <button
                  key={conversation.conversationId}
                  onClick={() =>
                    handleConversationClick(conversation.conversationId)
                  }
                  className={`tw:w-full tw:text-left tw:px-2 tw:py-1 tw:mx-2 tw:rounded-[5px] tw:cursor-pointer tw:transition-colors ${
                    isActive
                      ? "tw:bg-[#f6f8fa]"
                      : "tw:bg-transparent hover:tw:bg-black/5"
                  }`}
                  style={{ maxWidth: "calc(100% - 16px)" }}
                >
                  <div
                    className="tw:text-sm tw:font-medium tw:text-primary tw:truncate"
                    style={{ lineHeight: "24px" }}
                  >
                    {conversation.title || conversation.preview}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        /* Spacer - only when collapsed to push avatar to bottom */
        <div className="tw:flex-1" />
      )}

      {/* User avatar at bottom - only shown when signed in */}
      {isSignedIn && (
        <div className="tw:relative">
          <button
            aria-label="Sign out"
            onMouseEnter={() => setAvatarHovered(true)}
            onMouseLeave={() => setAvatarHovered(false)}
            className="tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:mb-2 tw:cursor-pointer tw:w-full"
          >
            <div
              className="tw:flex tw:items-center tw:justify-center tw:size-5 tw:rounded-full tw:text-[8px] tw:font-semibold tw:leading-[12px] tw:text-white tw:shrink-0"
              style={{ backgroundColor: theme.palette.text.primary }}
            >
              {initials}
            </div>
            <span
              className="tw:text-sm tw:font-medium tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150"
              style={{
                opacity: isExpanded ? 1 : 0,
                width: isExpanded ? "auto" : 0,
                color: theme.palette.text.primary,
              }}
            >
              Sign out
            </span>
          </button>
          {/* Tooltip for avatar when collapsed */}
          {!isExpanded && avatarHovered && (
            <div
              className="tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:bg-black tw:text-white tw:rounded-lg tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap"
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "18px",
              }}
            >
              Sign out
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
